/**
 * Utility functions for loading images as base64 data URLs.
 * Used by the Draft Share Card to avoid CORS issues with html-to-image.
 */

const PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPj/HwADBwIAMCbHYQAAAABJRU5ErkJggg==";

// Timeout per attempt
const FETCH_TIMEOUT_MS = 8000;
const MAX_RETRIES = 2;

/**
 * Single fetch attempt with timeout.
 */
async function fetchOnce(url, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      mode: "cors",
      signal: controller.signal,
    });
    clearTimeout(timer);

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.startsWith("image/")) {
      throw new Error(`Not an image: ${contentType}`);
    }
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // eslint-disable-line fp/no-mutation
      reader.onerror = reject; // eslint-disable-line fp/no-mutation
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    clearTimeout(timer);
    throw error;
  }
}

/**
 * Fetches an image URL and returns it as a base64 data URL.
 * Retries once on failure. Falls back to a dark placeholder.
 */
export async function fetchImageAsBase64(url) {
  // eslint-disable-next-line fp/no-let
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await fetchOnce(url, FETCH_TIMEOUT_MS);
    } catch (error) {
      if (attempt === MAX_RETRIES) {
        console.warn(
          `Image fetch failed after ${MAX_RETRIES} attempts (${url}):`,
          error.message,
        );
        return PLACEHOLDER;
      }
    }
  }
  return PLACEHOLDER;
}

/**
 * Pre-fetches an array of image URLs in parallel.
 * Returns a Map<url, base64DataUrl>.
 */
export async function prefetchImages(urls) {
  const results = await Promise.allSettled(
    urls.map(async (url) => ({
      url,
      data: await fetchImageAsBase64(url),
    })),
  );

  const map = new Map();
  results.forEach((result) => {
    if (result.status === "fulfilled") {
      map.set(result.value.url, result.value.data);
    }
  });
  return map;
}
